// ===========================================
// TAP TO PAY ON iPHONE (Stripe Terminal)
// ===========================================
// The iPhone itself is the card reader: guest taps their card/phone/watch on
// the top of the device. Flow: connection token (our API) -> connect the
// built-in reader once per session -> per payment: card_present intent ->
// collect -> confirm. Payments route to the restaurant's bank server-side
// exactly like every other payment.

import { Capacitor, registerPlugin } from '@capacitor/core';
import {
  StripeTerminal,
  TerminalConnectTypes,
  TerminalEventsEnum,
} from '@capacitor-community/stripe-terminal';
import { terminalApi } from './api';

export type TapToPayStatus =
  | 'connecting'   // initializing SDK / connecting the built-in reader
  | 'ready'        // reader connected, creating the payment
  | 'present'      // waiting for the guest to tap
  | 'processing';  // confirming with the bank

let initialized = false;

export function isTapToPaySupported(): boolean {
  // Tap to Pay is iPhone-only (XS+, iOS 16.4+); the OS/SDK enforces specifics.
  return Capacitor.getPlatform() === 'ios';
}

export type TapToPaySetupState = 'unsupported' | 'not_setup' | 'connecting' | 'ready';

/**
 * Current setup status, for the Settings/enablement UI. 'ready' means the
 * reader is connected (Apple T&C already accepted on this device).
 */
export async function getTapToPaySetupState(): Promise<TapToPaySetupState> {
  if (!isTapToPaySupported()) return 'unsupported';
  // Apple req 1.6: acceptance/readiness comes from the system (SDK), never a
  // stored variable — the local flag is only a banner-visibility hint.
  try {
    // Never let a stalled SDK hang the status check — cap it at 8s.
    const check = (async () => {
      await ensureInitialized();
      const { reader } = await StripeTerminal.getConnectedReader();
      return reader ? 'ready' : 'not_setup';
    })();
    const timeout = new Promise<TapToPaySetupState>((resolve) =>
      setTimeout(() => resolve('not_setup'), 8000)
    );
    return await Promise.race([check, timeout]);
  } catch {
    return 'not_setup';
  }
}

/**
 * Explicit enablement path (Settings → Set up Tap to Pay). Connecting the
 * reader the FIRST time is what triggers Apple's system flow: accept the
 * Tap to Pay on iPhone Terms & Conditions, then the "How to Tap" merchant
 * education. Resolves once the reader is connected and ready.
 */
export async function setupTapToPay(params?: {
  merchantDisplayName?: string;
  onStatus?: (s: TapToPaySetupState) => void;
}): Promise<void> {
  if (!isTapToPaySupported()) {
    throw new Error('Tap to Pay works on iPhone XS or newer (iOS 16.4+).');
  }
  params?.onStatus?.('connecting');
  await ensureReaderConnected(params?.merchantDisplayName);
  // Remember success so the awareness banner can hide without touching the
  // SDK on the main screen (avoids the connection-provider timeout there).
  try { localStorage.setItem('mise-ttp-setup-done', '1'); } catch { /* ignore */ }
  params?.onStatus?.('ready');
}

async function ensureInitialized() {
  if (initialized) return;

  // The connection-token PROVIDER must be registered BEFORE initialize() —
  // the SDK can request a token during/right after init, and if no listener
  // is registered yet it fails with a ConnectionTokenProvider error. (The
  // plugin's own example adds the listener first; we had it backwards.)
  await StripeTerminal.addListener(TerminalEventsEnum.RequestedConnectionToken, async () => {
    // A failed token here silently stalls the SDK mid-payment — retry once
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await terminalApi.connectionToken();
        if (res.success && res.data?.secret) {
          await StripeTerminal.setConnectionToken({ token: res.data.secret });
          return;
        }
      } catch (e) {
        console.error(`Terminal connection token failed (attempt ${attempt + 1}):`, e);
      }
      await new Promise((r) => setTimeout(r, 800));
    }
  });

  await StripeTerminal.initialize({ isTest: false });
  initialized = true;
}

// The connected-account id the reader was connected with — if the
// restaurant's Connect status changes mid-session, we must reconnect with
// the new value or collection fails.
let connectedOnBehalfOf: string | null | undefined = undefined;

async function ensureReaderConnected(merchantDisplayName?: string) {
  await ensureInitialized();

  // locationId + the connected account come from our backend
  const tokenRes = await terminalApi.connectionToken();
  if (!tokenRes.success) {
    throw new Error(tokenRes.error?.message || 'Could not reach the payment service.');
  }
  const onBehalfOf: string | null = tokenRes.data.onBehalfOf || null;

  const { reader: connected } = await StripeTerminal.getConnectedReader();
  if (connected) {
    if (connectedOnBehalfOf === onBehalfOf) return;
    // Connect status changed since we connected (e.g. bank just linked) —
    // reconnect so payments route correctly
    await StripeTerminal.disconnectReader().catch(() => {});
  }

  const { readers } = await StripeTerminal.discoverReaders({
    type: TerminalConnectTypes.TapToPay,
    locationId: tokenRes.data.locationId,
  });
  if (!readers || readers.length === 0) {
    // Apple req 1.4: when the OS is too old, tell the user to UPDATE iOS
    throw new Error(
      'Tap to Pay needs iOS 16.4 or later on iPhone XS or newer — update this iPhone in Settings → General → Software Update, then try again.'
    );
  }

  await StripeTerminal.connectReader({
    reader: readers[0],
    merchantDisplayName,
    // Charges are created on_behalf_of the restaurant's connected account —
    // Stripe requires the SAME account on the reader connection, or
    // collecting the payment fails with a config error.
    ...(onBehalfOf ? { onBehalfOf } : {}),
  });
  connectedOnBehalfOf = onBehalfOf;
}

/**
 * Take one Tap to Pay payment. Reports progress through onStatus so the UI
 * can show a calm, human overlay. Resolves with the paymentIntentId to record
 * in Mise (payments/confirm), or throws with a readable message.
 */
export async function takeTapToPayPayment(params: {
  checkId: string;
  amountCents: number;
  tipAmountCents: number;
  splitCheckId?: string;
  merchantDisplayName?: string;
  onStatus: (status: TapToPayStatus) => void;
}): Promise<{ paymentIntentId: string }> {
  const { checkId, amountCents, tipAmountCents, splitCheckId, merchantDisplayName, onStatus } = params;

  onStatus('connecting');
  await ensureReaderConnected(merchantDisplayName);

  onStatus('ready');
  const intentRes = await terminalApi.createPaymentIntent(checkId, amountCents, tipAmountCents, splitCheckId);
  if (!intentRes.success || !intentRes.data?.clientSecret) {
    throw new Error(intentRes.error?.message || 'Could not start the payment.');
  }

  onStatus('present');
  await StripeTerminal.collectPaymentMethod({ paymentIntent: intentRes.data.clientSecret });

  onStatus('processing');
  // The SDK's confirm can stall (e.g. its silent token refresh fails) and
  // 'Processing…' would hang forever. Time-box it: if the charge actually
  // landed despite the timeout, the payment_intent.succeeded webhook records
  // it server-side, so the honest message is 'check Recent Payments'.
  await withTimeout(
    StripeTerminal.confirmPaymentIntent(),
    45000,
    "The bank is taking unusually long. If the guest's card shows the charge, it will appear in Recent Payments automatically — don't charge again. Otherwise, retry."
  );

  return { paymentIntentId: intentRes.data.paymentIntentId };
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); }
    );
  });
}

/**
 * Background warm-up (Apple reqs 1.5 & 5.6): once a device has completed
 * setup, prepare the reader at app launch/foreground so the tap sheet opens
 * within a second at checkout. Fire-and-forget, never throws, and skipped
 * entirely on devices that never set up (avoids the first-run hang).
 */
export function warmUpTapToPay(merchantDisplayName?: string): void {
  if (!isTapToPaySupported()) return;
  try {
    if (localStorage.getItem('mise-ttp-setup-done') !== '1') return;
  } catch { return; }
  ensureReaderConnected(merchantDisplayName).catch(() => {
    // best-effort — the payment path will retry with full error handling
  });
}

/** Cancel a waiting-for-tap collection (guest changed their mind). */
export async function cancelTapToPay() {
  try {
    await StripeTerminal.cancelCollectPaymentMethod();
  } catch {
    // nothing was collecting — fine
  }
}

// ===========================================
// APPLE'S EDUCATION SHEET (ProximityReaderDiscovery, iOS 18+)
// ===========================================
const TapToPayEducation = registerPlugin<{ present(): Promise<{ presented: boolean }> }>(
  'TapToPayEducation'
);

/**
 * Present Apple's own "how to accept a tap" education sheet. Returns true if
 * the system sheet was shown; false when unavailable (iOS < 18, web) so the
 * caller can fall back to the in-app education screens.
 */
export async function presentAppleTapToPayEducation(): Promise<boolean> {
  if (Capacitor.getPlatform() !== 'ios') return false;
  try {
    const res = await TapToPayEducation.present();
    return !!res?.presented;
  } catch {
    return false;
  }
}
